import { useCallback, useMemo, useRef } from 'react';
import type { ChiTietPhieuNhapKho, PhieuNhapKhoAccountData, PhieuNhapKhoNhapXuatData } from '@/types/schemas';
import { createDefaultAccountData, createDefaultExportData, DEFAULT_MA_NX_CODE } from '../../../constants';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

type RowWithReason = ChiTietPhieuNhapKho;
type DefaultExportData = Partial<PhieuNhapKhoNhapXuatData> & {
  uuid?: string;
  ma_nx?: string;
  tk_data?: PhieuNhapKhoAccountData | null;
};

export function useDefaultExport() {
  const { entity } = useAuth();
  const fallbackExportData = useMemo(() => createDefaultExportData(undefined, DEFAULT_MA_NX_CODE), []);
  const cachedDefaultRef = useRef<DefaultExportData | null>(null);

  const fetchDefaultExportData = useCallback(async () => {
    if (cachedDefaultRef.current) return cachedDefaultRef.current;

    if (!entity?.slug) {
      cachedDefaultRef.current = fallbackExportData;
      return cachedDefaultRef.current;
    }

    try {
      const response = await api.get(`/entities/${entity.slug}/erp/${QUERY_KEYS.LY_DO_NHAP_XUAT}/`, {
        params: { search: DEFAULT_MA_NX_CODE, page_size: 1 }
      });
      const list = response.data?.results ?? response.data ?? [];
      const matched = Array.isArray(list)
        ? (list as DefaultExportData[]).find(item => item.ma_nx === DEFAULT_MA_NX_CODE) || list[0]
        : null;

      cachedDefaultRef.current = matched || fallbackExportData;
    } catch (error) {
      console.warn('Failed to fetch default ma_nx_data, using fallback', error);
      cachedDefaultRef.current = fallbackExportData;
    }

    return cachedDefaultRef.current;
  }, [entity?.slug, fallbackExportData]);

  const applyDefaultExportDataToRows = useCallback(
    async <T extends RowWithReason>(
      rows: T[],
      options?: { setAccount?: boolean; applyReason?: boolean }
    ): Promise<T[]> => {
      const { setAccount = false, applyReason = false } = options || {};
      const defaultExportData = await fetchDefaultExportData();
      if (!rows?.length || !defaultExportData) return rows;

      if (!applyReason && !setAccount) {
        return rows;
      }

      return rows.map(row => {
        const next: T = { ...row };

        // Set default ma_nx if missing
        if (applyReason && !next.ma_nx && !next.ma_nx_data) {
          next.ma_nx = defaultExportData.uuid;
          next.ma_nx_data = defaultExportData as PhieuNhapKhoNhapXuatData;
        }

        // Optionally set default tk_du (TK có) for create mode only if user hasn't chosen
        if (setAccount) {
          const hasUserTk = Boolean(next.tk_du_data) || Boolean(next.tk_du);
          if (!hasUserTk) {
            const accountDefault =
              defaultExportData.tk_data ||
              createDefaultAccountData(defaultExportData.uuid, defaultExportData.ma_nx || DEFAULT_MA_NX_CODE);
            if (accountDefault) next.tk_du_data = accountDefault;
          }
        }

        return next as T;
      });
    },
    [fetchDefaultExportData]
  );

  return { fetchDefaultExportData, applyDefaultExportDataToRows };
}
